import { useQuery } from '@tanstack/react-query';
import { AdvancedFilterPanel, type FilterField } from '@/components/common/AdvancedFilter';
import {
  areaManagersQueryOptions,
  zonalManagersQueryOptions,
} from '@/features/users/api/users.api';
import { useGeoCascade } from '@/hooks/useGeoCascade';
import { getFilterPanelProps, type UrlSyncedFilters } from '@/hooks/useUrlSyncedFilters';
import {
  getDateFromToFilterFields,
  getGeoFilterFields,
  getReportingMemberFilterField,
  getZonalManagerFilterField,
} from '@/utils/filterFields';
import { FILTER_KEYS, parseNumberFilter } from '@/utils/filters';

const NO_FIELDS: FilterField[] = [];

interface BillingFilterPanelProps {
  filters: UrlSyncedFilters;
  fieldsBeforeGeo?: FilterField[] | undefined;
  fieldsAfterGeo?: FilterField[] | undefined;
}

export function BillingFilterPanel({
  filters,
  fieldsBeforeGeo = NO_FIELDS,
  fieldsAfterGeo = NO_FIELDS,
}: BillingFilterPanelProps) {
  const { filterValues } = filters;

  const { data: reportingMembers } = useQuery(areaManagersQueryOptions());
  const { data: zonalManagers } = useQuery(zonalManagersQueryOptions());
  const geoOptions = useGeoCascade({
    divisionId: parseNumberFilter(filterValues[FILTER_KEYS.divisionId]),
    districtId: parseNumberFilter(filterValues[FILTER_KEYS.districtId]),
  });

  const fields: FilterField[] = [
    ...getDateFromToFilterFields(),
    getReportingMemberFilterField(reportingMembers),
    getZonalManagerFilterField(zonalManagers),
    ...fieldsBeforeGeo,
    ...getGeoFilterFields(filterValues, geoOptions),
    ...fieldsAfterGeo,
  ];

  return <AdvancedFilterPanel fields={fields} {...getFilterPanelProps(filters)} />;
}
